/**
 * The `wall` autotile kind: building walls (wood, stone, ...) as data.
 *
 * A wall definition says, for each autotile index a material can draw, which tiles to emit at which
 * offsets from the wall's cell (the ANCHOR) -- a lone wall is a cap one row up and a base on the
 * cell -- and, where the art depends on more than the index, which alternative to take. HouseBuilder
 * (house/feature/house.ts) asks `resolveWallCell` for every wall cell, in row-major order, and writes
 * what it returns through its own entry()/addFor(); Sprite Lab asks the same function.
 *
 * Every emitted tile is grouped under the anchor as its PARENT (HouseBuilder's `addFor(x, y)`): one
 * wall = one object, one HP counter, one coordinateCache key, one sort row. `parent: "self"` makes a
 * tile its own object instead (HouseBuilder's `add`).
 *
 * Two of HouseBuilder's behaviours shape what a rule means, and are kept by construction:
 *  - its entry() keeps ONE tile per cell+layer, so a second emit onto a cell re-points the tile that
 *    is already there (only its tile position changes; its tileset stays) rather than adding one;
 *  - `exists` sees only tiles emitted EARLIER -- by cells before this one in row-major order, by
 *    earlier steps of this cell's own rule, or by anything else HouseBuilder put on the layer first.
 *    So the order of a rule's steps is part of the data.
 *
 * Conditions read the index grid of EVERY wall cell, whatever its material: one house grid holds
 * all of them, so a wooden wall joins a stone one.
 *
 * The indices a definition has a rule for are the ones it can draw; any other index is a shape the
 * material cannot render, and placement refuses it (autotiler.ts, COMPUTABLE_WALL_AUTOTILE_INDICES).
 *
 * `itemType` links a material to the item that builds it (HouseBuilder.wallMaterials is derived from
 * it); content validation requires that item to exist with buildType "wall", and no two materials to
 * name the same item.
 *
 * A leaf like autotile_library.ts (which validates and loads these): no models, no mechanics, no
 * map builder, no file system. Only types are imported.
 */
import type { TileRef } from "./autotile_library"

// ---------------------------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------------------------

/** [dx, dy] from the wall's cell. Each is -1, 0 or 1: a wall's pieces stay in its neighbourhood. */
export type WallOffset = [number, number]

/** One tile, at an offset from the wall's cell, on the definition's layer and tileset. */
export interface WallEmit {
    dx?: number
    dy?: number
    /** Tile position in the definition's tileset. */
    tile: number
    /** Omitted: grouped under the wall's cell. "self": the tile is its own object. */
    parent?: "self"
}

export type WallCondition =
    /** There is a wall (any material) at the offset; with `index`, its autotile index is one of these. */
    | { neighbour: WallOffset; index?: number[] }
    /** A tile is already on the definition's layer at the offset (see the module note on order). */
    | { exists: WallOffset }
    | { all: WallCondition[] }
    | { any: WallCondition[] }
    | { not: WallCondition }

/** One alternative of a choice: taken when `when` holds (no `when`: always, so it must be last). */
export interface WallAlternative {
    when?: WallCondition
    emit: WallEmit[]
}

/** The first alternative whose condition holds is emitted; if none holds, nothing is. */
export interface WallChoice {
    choose: WallAlternative[]
}

export type WallStep = WallEmit | WallChoice

/** What goes under every cell of the wall, besides its art. Omitted: nothing on that layer. */
export interface WallCellDecorations {
    /** The collision marker (HouseBuilder's `blocking` layer). */
    blocking?: TileRef
    /** The line-of-sight marker (HouseBuilder's `occlusion` layer). */
    occlusion?: TileRef
}

export interface WallAutotileDefinition {
    id: string
    name: string
    kind: "wall"
    /** The tileset every emitted tile is a position in. */
    tilesetId: string
    /** The layer the art goes on (and the one `exists` asks about). */
    layerId: string
    cell: WallCellDecorations
    /** The item that builds this material (an item with buildType "wall"). Omitted: no item builds it. */
    itemType?: string
    /** Autotile index (1-47) -> the steps that draw it, in order. An index with no rule cannot be drawn. */
    rules: Record<string, WallStep[]>
    /** Why each index draws what it draws, shown by the Lab. */
    notes?: Record<string, string>
}

export const isWallChoice = (step: WallStep): step is WallChoice => (step as WallChoice).choose !== undefined

// ---------------------------------------------------------------------------------------------
// Validation (validateAutotileFile calls this for kind "wall")
// ---------------------------------------------------------------------------------------------

export interface WallValidationHelpers {
    fail: (message: string) => void
    /** Checks a position in the definition's own tileset. */
    tile: (label: string, value: unknown) => void
    /** Checks a tile reference into any tileset. */
    tileRef: (label: string, value: unknown) => void
    layer: (label: string, value: unknown, required: boolean) => void
}

const isInt = (value: unknown): value is number => typeof value === "number" && Number.isInteger(value)
const isIndex = (value: unknown) => isInt(value) && value >= 1 && value <= 47
const isObject = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value)

const EMIT_KEYS = ["dx", "dy", "tile", "parent"]
const ALTERNATIVE_KEYS = ["when", "emit"]
const CONDITION_FORMS = ["neighbour", "exists", "all", "any", "not"]

export const validateWallDefinition = (definition: WallAutotileDefinition, { fail, tile, tileRef, layer }: WallValidationHelpers) => {
    layer("layerId", definition.layerId, true)
    if (definition.itemType !== undefined && (typeof definition.itemType !== "string" || !/^[A-Za-z0-9][A-Za-z0-9_.-]*$/.test(definition.itemType))) {
        fail(`itemType must be an item id, got ${JSON.stringify(definition.itemType)}`)
    }

    if (!isObject(definition.cell)) {
        fail("cell must say what goes under every wall cell ({} for nothing)")
    } else {
        Object.keys(definition.cell).forEach(key => {
            if (key !== "blocking" && key !== "occlusion") fail(`cell: unknown decoration "${key}" (expected blocking, occlusion)`)
            else tileRef(`cell.${key}`, definition.cell[key])
        })
    }

    const offset = (label: string, value: unknown, { allowSelf }: { allowSelf: boolean }) => {
        if (!Array.isArray(value) || value.length !== 2 || !value.every(isInt)) {
            fail(`${label} must be an offset [dx, dy] of integers, got ${JSON.stringify(value)}`)
        } else if (value.some(d => d < -1 || d > 1)) {
            fail(`${label} ${JSON.stringify(value)} is outside the wall's neighbourhood (each of dx, dy is -1, 0 or 1)`)
        } else if (!allowSelf && value[0] === 0 && value[1] === 0) {
            fail(`${label} [0, 0] is the wall's own cell`)
        }
    }

    const emit = (label: string, value: unknown) => {
        if (!isObject(value)) {
            fail(`${label} must be a tile to emit ({ "dx", "dy", "tile", "parent" })`)
            return
        }
        Object.keys(value).forEach(key => {
            if (!EMIT_KEYS.includes(key)) fail(`${label}: unknown field "${key}" (expected ${EMIT_KEYS.join(", ")})`)
        })
        ;(["dx", "dy"] as const).forEach(axis => {
            const d = value[axis]
            if (d === undefined) return
            if (!isInt(d)) fail(`${label}.${axis} must be an integer, got ${JSON.stringify(d)}`)
            else if (d < -1 || d > 1) fail(`${label}.${axis} ${d} is outside the wall's neighbourhood (-1, 0 or 1)`)
        })
        tile(`${label}.tile`, value.tile)
        if (value.parent !== undefined && value.parent !== "self") fail(`${label}.parent must be "self" or left out, got ${JSON.stringify(value.parent)}`)
    }

    const condition = (label: string, value: unknown, depth = 0) => {
        if (depth > 8) {
            fail(`${label}: conditions nest too deep`)
            return
        }
        if (!isObject(value)) {
            fail(`${label} must be a condition (one of ${CONDITION_FORMS.join(", ")})`)
            return
        }
        const forms = Object.keys(value).filter(key => CONDITION_FORMS.includes(key))
        const unknown = Object.keys(value).filter(key => !CONDITION_FORMS.includes(key) && !(key === "index" && forms[0] === "neighbour"))
        if (forms.length !== 1) {
            fail(`${label}: unknown condition form ${JSON.stringify(Object.keys(value))} (expected exactly one of ${CONDITION_FORMS.join(", ")})`)
            return
        }
        unknown.forEach(key => fail(`${label}: unknown field "${key}" in a ${forms[0]} condition`))
        switch (forms[0]) {
            case "neighbour":
                offset(`${label}.neighbour`, value.neighbour, { allowSelf: false })
                if (value.index !== undefined) {
                    if (!Array.isArray(value.index) || value.index.length === 0) fail(`${label}.index must be a non-empty list of autotile indices`)
                    else value.index.forEach((i, n) => !isIndex(i) && fail(`${label}.index[${n}] ${JSON.stringify(i)} is not an autotile index (1-47)`))
                }
                break
            case "exists":
                offset(`${label}.exists`, value.exists, { allowSelf: true })
                break
            case "all":
            case "any": {
                const parts = value[forms[0]]
                if (!Array.isArray(parts) || parts.length === 0) fail(`${label}.${forms[0]} must be a non-empty list of conditions`)
                else parts.forEach((part, n) => condition(`${label}.${forms[0]}[${n}]`, part, depth + 1))
                break
            }
            case "not":
                condition(`${label}.not`, value.not, depth + 1)
                break
        }
    }

    if (!isObject(definition.rules)) {
        fail("rules must map autotile indices to lists of steps")
    } else {
        if (Object.keys(definition.rules).length === 0) fail("rules is empty: the material could draw nothing")
        Object.entries(definition.rules).forEach(([index, steps]) => {
            const where = `rules[${index}]`
            if (!/^\d+$/.test(index) || !isIndex(Number(index))) fail(`rules: "${index}" is not an autotile index (1-47)`)
            if (!Array.isArray(steps) || steps.length === 0) {
                fail(`${where} must be a non-empty list of steps (leave the index out if the material cannot draw it)`)
                return
            }
            steps.forEach((step, s) => {
                const at = `${where}[${s}]`
                if (isObject(step) && "choose" in step) {
                    Object.keys(step).forEach(key => key !== "choose" && fail(`${at}: unknown field "${key}" beside "choose"`))
                    const alternatives = step.choose
                    if (!Array.isArray(alternatives) || alternatives.length === 0) {
                        fail(`${at}.choose must be a non-empty list of alternatives`)
                        return
                    }
                    alternatives.forEach((alternative, a) => {
                        const alt = `${at}.choose[${a}]`
                        if (!isObject(alternative)) {
                            fail(`${alt} must be an alternative ({ "when", "emit" })`)
                            return
                        }
                        Object.keys(alternative).forEach(key => {
                            if (!ALTERNATIVE_KEYS.includes(key)) fail(`${alt}: unknown field "${key}" (expected when, emit)`)
                        })
                        if (alternative.when === undefined) {
                            if (a !== alternatives.length - 1) fail(`${alt} has no "when", so the alternatives after it can never be taken`)
                        } else {
                            condition(`${alt}.when`, alternative.when)
                        }
                        if (!Array.isArray(alternative.emit)) fail(`${alt}.emit must be a list of tiles ([] for none)`)
                        else alternative.emit.forEach((e, n) => emit(`${alt}.emit[${n}]`, e))
                    })
                } else {
                    emit(at, step)
                }
            })
        })
    }

    if (definition.notes !== undefined) {
        if (!isObject(definition.notes)) fail("notes must map autotile indices to strings")
        else Object.entries(definition.notes).forEach(([index, note]) => {
            if (!isIndex(Number(index)) || typeof note !== "string") fail(`notes: "${index}" must be an index with a string note`)
        })
    }
}

// ---------------------------------------------------------------------------------------------
// Resolution: what one wall cell emits
// ---------------------------------------------------------------------------------------------

/** One tile a wall cell writes, in the order it writes them. */
export interface WallWrite {
    /** The tile's own cell. */
    x: number
    y: number
    dx: number
    dy: number
    layerId: string
    tilesetId: string
    tilePos: number
    /** The cell the tile is grouped under. */
    parent: { x: number; y: number }
    parentMode: "anchor" | "self"
    /** The rule step that wrote it, and inside a choice the alternative taken. */
    step: number
    alternative?: number
}

/** A condition with its verdict, for the Lab's inspector. */
export interface WallConditionTrace {
    text: string
    holds: boolean
    parts?: WallConditionTrace[]
}

export interface WallChoiceTrace {
    step: number
    /** The alternative taken, or undefined when none held. */
    chosen?: number
    alternatives: { when?: WallConditionTrace; holds: boolean; emits: number }[]
}

export interface WallCellResolution {
    x: number
    y: number
    index: number
    /** The definition has a rule for this index. */
    drawable: boolean
    writes: WallWrite[]
    /** Every choice the rule made. Conditions are described only when asked for (`trace`). */
    choices: WallChoiceTrace[]
}

export interface WallCellContext {
    /** Autotile index of every wall cell, [y][x], all materials together (autotiler.ts `autotile`). */
    indices: ArrayLike<ArrayLike<number>>
    /** Whether a tile is already on `layerId` at (x, y). HouseBuilder: its entry cache. */
    exists: (x: number, y: number, layerId: string) => boolean
}

export interface WallResolveOptions {
    /** Describe every condition evaluated (the Lab). Off for the game. */
    trace?: boolean
}

const signed = (d: number) => (d > 0 ? `+${d}` : `${d}`)
const offsetText = ([dx, dy]: WallOffset) => `(${signed(dx)}, ${signed(dy)})`

/** The rule a definition has for an index, if any. */
export const wallRule = (definition: WallAutotileDefinition, index: number): WallStep[] | undefined => definition.rules[String(index)]

/** The indices a definition can draw, ascending: exactly the ones it has a rule for. */
export const wallRuleIndices = (definition: WallAutotileDefinition): number[] =>
    Object.keys(definition.rules)
        .map(Number)
        .sort((a, b) => a - b)

export const resolveWallCell = (
    definition: WallAutotileDefinition,
    x: number,
    y: number,
    context: WallCellContext,
    options: WallResolveOptions = {},
): WallCellResolution => {
    const index = context.indices[y]?.[x] ?? 0
    const rule = wallRule(definition, index)
    const writes: WallWrite[] = []
    const choices: WallChoiceTrace[] = []
    if (!rule) return { x, y, index, drawable: false, writes, choices }

    const { layerId, tilesetId } = definition
    // Tiles this cell has already written count as existing, exactly as they would in HouseBuilder's
    // cache had each write been applied as it was made.
    const written = new Set<string>()
    const exists = (cx: number, cy: number) => written.has(`${cx},${cy}`) || context.exists(cx, cy, layerId)
    const indexAt = (cx: number, cy: number) => context.indices[cy]?.[cx] ?? 0

    const evaluate = (condition: WallCondition): WallConditionTrace => {
        const describe = options.trace
        if ("neighbour" in condition) {
            const [dx, dy] = condition.neighbour
            const there = indexAt(x + dx, y + dy)
            const holds = condition.index ? condition.index.includes(there) : !!there
            const text = describe
                ? condition.index
                    ? `neighbour ${offsetText(condition.neighbour)} has index ${there || "none"}; wanted ${condition.index.join(" or ")}`
                    : `a wall at ${offsetText(condition.neighbour)}: ${there ? `yes (index ${there})` : "no"}`
                : ""
            return { text, holds }
        }
        if ("exists" in condition) {
            const [dx, dy] = condition.exists
            const holds = exists(x + dx, y + dy)
            return { text: describe ? `a ${layerId} tile already at ${offsetText(condition.exists)}: ${holds ? "yes" : "no"}` : "", holds }
        }
        if ("not" in condition) {
            const inner = evaluate(condition.not)
            return { text: describe ? "not" : "", holds: !inner.holds, parts: [inner] }
        }
        const all = "all" in condition
        const parts = (all ? (condition as { all: WallCondition[] }).all : (condition as { any: WallCondition[] }).any).map(evaluate)
        return { text: describe ? (all ? "all of" : "any of") : "", holds: all ? parts.every(p => p.holds) : parts.some(p => p.holds), parts }
    }

    const emit = (e: WallEmit, step: number, alternative?: number) => {
        const dx = e.dx || 0
        const dy = e.dy || 0
        const self = e.parent === "self"
        writes.push({
            x: x + dx,
            y: y + dy,
            dx,
            dy,
            layerId,
            tilesetId,
            tilePos: e.tile,
            parent: self ? { x: x + dx, y: y + dy } : { x, y },
            parentMode: self ? "self" : "anchor",
            step,
            ...(alternative === undefined ? {} : { alternative }),
        })
        written.add(`${x + dx},${y + dy}`)
    }

    rule.forEach((step, s) => {
        if (!isWallChoice(step)) {
            emit(step, s)
            return
        }
        const trace: WallChoiceTrace = { step: s, alternatives: [] }
        step.choose.forEach((alternative, a) => {
            // Conditions only read, so describing the ones after the chosen alternative changes nothing.
            if (trace.chosen !== undefined && !options.trace) return
            const verdict = alternative.when ? evaluate(alternative.when) : undefined
            const holds = verdict ? verdict.holds : true
            trace.alternatives.push({ ...(verdict && options.trace ? { when: verdict } : {}), holds, emits: alternative.emit.length })
            if (holds && trace.chosen === undefined) trace.chosen = a
        })
        choices.push(trace)
        if (trace.chosen !== undefined) step.choose[trace.chosen].emit.forEach(e => emit(e, s, trace.chosen))
    })
    return { x, y, index, drawable: true, writes, choices }
}

/** The four BuilderUtils a wall needs, as HouseBuilder hands them out (see BuilderUtils in house.ts). */
export interface WallBuilderUtils<E> {
    entry: (x: number, y: number, tilePos: number, layerId: string, tilesetId: string) => E
    addFor: (x: number, y: number, entry: E) => void
    exists: (x: number, y: number, layerId: string) => boolean
}

/**
 * Resolve one wall cell and write it: entry() each tile, addFor() it under its parent, in order.
 * HouseBuilder and Sprite Lab both run walls through this.
 */
export const applyWallCell = <E>(
    definition: WallAutotileDefinition,
    indices: ArrayLike<ArrayLike<number>>,
    x: number,
    y: number,
    utils: WallBuilderUtils<E>,
    options?: WallResolveOptions,
): WallCellResolution => {
    const resolution = resolveWallCell(definition, x, y, { indices, exists: utils.exists }, options)
    resolution.writes.forEach(w => utils.addFor(w.parent.x, w.parent.y, utils.entry(w.x, w.y, w.tilePos, w.layerId, w.tilesetId)))
    return resolution
}
